import { Button, Form, Input, Modal } from 'antd';
import { useState } from 'react';
import { OverrideApproverSelect } from './OverrideApproverSelect';
import type { ReadinessItem } from '../../../jobs.api';

interface OverrideFormValues {
  approverId: string;
  reason: string;
}

interface ReadinessOverrideModalProps {
  item: ReadinessItem | null;
  onCancel: () => void;
  onSubmit: (approverId: string, reason: string) => Promise<boolean>;
}

/**
 * A supervisor's override on one not-met readiness item (gitaumoses4/chukua#36) — a real, distinct
 * second person who holds `override.approve`, not the self-service correction `JobAccessorialOverrideModal`
 * is. Built separately rather than copied from it: different primitive, different shape.
 */
export function ReadinessOverrideModal({ item, onCancel, onSubmit }: ReadinessOverrideModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<OverrideFormValues>();

  async function submit(values: OverrideFormValues) {
    setSubmitting(true);
    try {
      if (await onSubmit(values.approverId, values.reason)) form.resetFields();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={item !== null} title={`Override: ${item?.label ?? ''}`} onCancel={onCancel} footer={null} destroyOnClose>
      <Form<OverrideFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="approverId" label="Approver" rules={[{ required: true, message: 'Pick who holds override approval' }]}>
          <OverrideApproverSelect />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Enter a reason' }]}>
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
        <Form.Item className="mb-0 flex justify-end">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Record override
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
